'use client';

import { c as _c } from "react/compiler-runtime";
import CancelIcon from '@precis-digital/kurama/icons/CancelIcon';
import CheckIcon from '@precis-digital/kurama/icons/CheckIcon';
import KuramaToaster, { type ToasterProps, type ToasterVariant } from '@precis-digital/kurama/Toaster';
import { useEffect, useRef, useState, type ReactNode } from 'react';
const isClientEnvironment = (): boolean => globalThis.window !== undefined;
interface MakeToastOptions {
  onClose?: () => void;
  onConfirm?: () => void;
  persistThroughRouteChange?: boolean;
  onUndo?: () => void;
}
interface MakeErrorToastOptions extends MakeToastOptions {
  headerMessage?: string;
}
interface MakeSuccessToastOptions extends MakeToastOptions {
  confirmToClose?: boolean;
  headerMessage?: string;
}
interface MakeToastWithPromiseHandlers {
  toastOnSuccess: (message: string | ReactNode, successOptions?: MakeSuccessToastOptions) => void;
  toastOnError: (message: string | ReactNode, successOptions?: MakeErrorToastOptions) => void;
  cancelToast: () => void;
}
interface ToasterOptions {
  open: boolean;
  isLoading: boolean;
  variant: ToasterVariant;
  description: string | ReactNode;
  successDelay?: number;
  onClose?: () => void;
  onConfirm?: () => void;
  persistThroughRouteChange?: boolean;
  headerMessage?: string;
  onUndo?: () => void;
}
const defaultToasterOptions: ToasterOptions = {
  open: false,
  isLoading: false,
  variant: 'success',
  description: '',
  successDelay: undefined,
  onClose: undefined,
  onConfirm: undefined,
  persistThroughRouteChange: false,
  onUndo: undefined
};
let memoryState: ToasterOptions = {
  ...defaultToasterOptions
};
type Listener = (state: ToasterOptions) => void;
type ReducerActionType = 'SHOW_TOAST' | 'CLOSE_TOAST';
interface ReducerAction {
  type: ReducerActionType;
  open?: boolean;
  isLoading?: boolean;
  variant?: ToasterVariant;
  description?: string | ReactNode;
  successDelay?: number;
  onClose?: () => void;
  onConfirm?: () => void;
  persistThroughRouteChange?: boolean;
  headerMessage?: string;
  onUndo?: () => void;
}
const listeners: Listener[] = [];
const dispatch = (action: ReducerAction): void => {
  memoryState = reducer(memoryState, action);
  listeners.forEach(listener => {
    listener(memoryState);
  });
};
const addListener = (listener: Listener): (() => void) => {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
};
const reducer = (state: ToasterOptions, action: ReducerAction): ToasterOptions => {
  switch (action.type) {
    case 'SHOW_TOAST':
      return {
        ...state,
        open: true,
        isLoading: action?.isLoading ?? false,
        variant: action?.variant ?? state.variant,
        description: action?.description,
        persistThroughRouteChange: action?.persistThroughRouteChange ?? false,
        successDelay: action.successDelay,
        onClose: action.onClose,
        onConfirm: action.onConfirm,
        headerMessage: action.headerMessage,
        onUndo: action.onUndo
      };
    case 'CLOSE_TOAST':
      if (state.onClose != null) state.onClose();
      return {
        ...state,
        ...defaultToasterOptions,
        variant: state.variant,
        description: state.description,
        successDelay: state.successDelay,
        headerMessage: state.headerMessage
      };
    default:
      return state;
  }
};
export const makeSuccessToast = (message: string | ReactNode, options?: MakeSuccessToastOptions): void => {
  const action: ReducerAction = {
    type: 'SHOW_TOAST',
    isLoading: false,
    variant: 'success',
    description: message,
    headerMessage: options?.headerMessage
  };

  // When an undo handler is provided, keep the toast open until the user dismisses
  // or clicks undo so the auto-dismiss progress bar doesn't race the undo button.
  if (options?.confirmToClose !== true && options?.onUndo == null) {
    action.successDelay = 1500;
  }
  if (options?.onClose != null) {
    action.onClose = options.onClose;
  }
  if (options?.onConfirm != null) {
    action.onConfirm = options.onConfirm;
  }
  if (options?.persistThroughRouteChange === true) {
    action.persistThroughRouteChange = true;
  }
  if (options?.onUndo != null) {
    action.onUndo = options.onUndo;
  }
  dispatch(action);
};
export const makeErrorToast = (message: string | ReactNode, options?: MakeErrorToastOptions): void => {
  const action: ReducerAction = {
    type: 'SHOW_TOAST',
    isLoading: false,
    variant: 'error',
    description: message,
    headerMessage: options?.headerMessage
  };
  if (options?.onClose != null) {
    action.onClose = options.onClose;
  }
  if (options?.onConfirm != null) {
    action.onConfirm = options.onConfirm;
  }
  if (options?.persistThroughRouteChange === true) {
    action.persistThroughRouteChange = true;
  }
  if (options?.onUndo != null) {
    action.onUndo = options.onUndo;
  }
  dispatch(action);
};
export const makeToastWithLoading = (options?: MakeToastOptions & {
  headerMessage?: string;
}): MakeToastWithPromiseHandlers => {
  const toastOnSuccess = (successMessage: string | ReactNode, successOptions?: MakeSuccessToastOptions): void => {
    const toastOptions = {
      ...options,
      ...successOptions
    };
    makeSuccessToast(successMessage, toastOptions);
  };
  const toastOnError = (errorMessage: string | ReactNode, errorOptions?: MakeErrorToastOptions): void => {
    const toastOptions = {
      ...options,
      ...errorOptions
    };
    makeErrorToast(errorMessage, toastOptions);
  };
  const cancelToast = (): void => {
    closeToaster();
  };
  const action: ReducerAction = {
    type: 'SHOW_TOAST',
    isLoading: true,
    headerMessage: options?.headerMessage
  };
  if (options?.onClose != null) {
    action.onClose = options.onClose;
  }
  if (options?.onConfirm != null) {
    action.onConfirm = options.onConfirm;
  }
  if (options?.persistThroughRouteChange === true) {
    action.persistThroughRouteChange = true;
  }
  if (options?.onUndo != null) {
    action.onUndo = options.onUndo;
  }
  dispatch(action);
  return {
    toastOnSuccess,
    toastOnError,
    cancelToast
  };
};
export const closeToaster = (): void => {
  dispatch({
    type: 'CLOSE_TOAST'
  });
};
export const Toaster = () => {
  const $ = _c(26);
  const [toasterState, setToasterState] = useState(memoryState);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const removeListener = addListener(setToasterState);
      return () => {
        removeListener();
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const locationOnLoad = useRef(isClientEnvironment() ? window.location.pathname : null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      locationOnLoad.current = isClientEnvironment() ? window.location.pathname : null;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== toasterState) {
    t3 = [toasterState];
    $[3] = toasterState;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  const locationNow = isClientEnvironment() ? window.location.pathname : null;
  let t4;
  let t5;
  if ($[5] !== toasterState.persistThroughRouteChange) {
    t4 = () => {
      if (toasterState.persistThroughRouteChange !== true && locationOnLoad.current !== locationNow && locationOnLoad != null && locationNow != null) {
        closeToaster();
      }
    };
    t5 = [toasterState.persistThroughRouteChange, locationNow, locationOnLoad];
    $[5] = toasterState.persistThroughRouteChange;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      headerMessage: "Woo-hoo!",
      headerIcon: <CheckIcon />,
      confirmButtonMessage: "Ok, Great!"
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      success: t6,
      error: {
        headerMessage: "Oh snap!",
        headerIcon: <CancelIcon />,
        confirmButtonMessage: "Ok, I understand"
      }
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const toasterVariantProps = t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      loadingMessage: "Processing..."
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const baseToasterProps = t8;
  let t9;
  if ($[11] !== toasterState) {
    t9 = toasterState.onConfirm != null ? () => toasterState.onConfirm?.() : undefined;
    $[11] = toasterState;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = toasterState.headerMessage ?? toasterVariantProps[toasterState.variant].headerMessage;
  const t11 = toasterVariantProps[toasterState.variant];
  const t12 = toasterState.onUndo == null ? toasterVariantProps[toasterState.variant].confirmButtonMessage : undefined;
  let t13;
  if ($[13] !== toasterState) {
    t13 = toasterState.onUndo == null ? undefined : () => toasterState.onUndo?.();
    $[13] = toasterState;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== t10 || $[16] !== t11.headerIcon || $[17] !== t12 || $[18] !== t13 || $[19] !== t9 || $[20] !== toasterState.description || $[21] !== toasterState.isLoading || $[22] !== toasterState.open || $[23] !== toasterState.successDelay || $[24] !== toasterState.variant) {
    t14 = <KuramaToaster open={toasterState.open} loading={toasterState.isLoading} variant={toasterState.variant} onConfirm={t9} onClose={closeToaster} description={toasterState.description} successDelay={toasterState.successDelay} headerMessage={t10} headerIcon={t11.headerIcon} confirmButtonMessage={t12} onUndo={t13} {...baseToasterProps} />;
    $[15] = t10;
    $[16] = t11.headerIcon;
    $[17] = t12;
    $[18] = t13;
    $[19] = t9;
    $[20] = toasterState.description;
    $[21] = toasterState.isLoading;
    $[22] = toasterState.open;
    $[23] = toasterState.successDelay;
    $[24] = toasterState.variant;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  return t14;
};
export default Toaster;